import React, { createContext, useContext, useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithPopup, 
  GoogleAuthProvider, 
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { createLocalAuthUser, isLocalAuthFallbackEnabled } from './authLocalFallback';
import { isAdminEmail } from './authAdmin';

// Firebase configuration - replace with your own config
/*
const firebaseConfig = {
  apiKey: "YOUR_API_KEY",
  authDomain: "YOUR_AUTH_DOMAIN",
  projectId: "YOUR_PROJECT_ID",
  storageBucket: "YOUR_STORAGE_BUCKET",
  messagingSenderId: "YOUR_MESSAGING_SENDER_ID",
  appId: "YOUR_APP_ID"
};
*/
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "",
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "",
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "",
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
    appId: process.env.REACT_APP_FIREBASE_APP_ID || "",
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || ""
  };

const hasFirebaseConfig = Boolean(
  firebaseConfig.apiKey &&
  firebaseConfig.authDomain &&
  firebaseConfig.projectId &&
  firebaseConfig.appId
);

// Initialize Firebase only when public config exists. Local development/test
// environments may omit Firebase credentials; the app should still render instead
// of crashing with auth/invalid-api-key before React mounts.
const app = hasFirebaseConfig ? initializeApp(firebaseConfig) : null;
const auth = app ? getAuth(app) : null;
const db = app ? getFirestore(app) : null;
const provider = app ? new GoogleAuthProvider() : null;
const localAuthFallbackEnabled = isLocalAuthFallbackEnabled({
  hasFirebaseConfig,
  hostname: typeof window !== 'undefined' ? window.location.hostname : '',
});

const requireAuth = () => {
  if (!auth && !localAuthFallbackEnabled) {
    throw new Error('Authentication is not configured for this environment.');
  }
};

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const E2E_AUTH_ENABLED = process.env.REACT_APP_E2E_AUTH === 'true';
const E2E_USER = {
  uid: 'playwright-e2e-user',
  email: 'playwright-e2e@example.com',
  displayName: 'Playwright E2E User',
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(E2E_AUTH_ENABLED ? E2E_USER : null);
  const [isAdmin, setIsAdmin] = useState(E2E_AUTH_ENABLED);
  const [isLoading, setIsLoading] = useState(!E2E_AUTH_ENABLED && hasFirebaseConfig);

  useEffect(() => {
    if (E2E_AUTH_ENABLED || !auth) return undefined;

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setIsAdmin(user ? isAdminEmail(user.email) : false);
      setIsLoading(false);
    });

    return unsubscribe;
  }, []);

  // Google sign-in
  const login = async () => {
    try {
      if (localAuthFallbackEnabled && !auth) {
        const localUser = createLocalAuthUser();
        setCurrentUser(localUser);
        setIsAdmin(true);
        toast.success('Signed in with local development auth.');
        return localUser;
      }
      requireAuth();
      const result = await signInWithPopup(auth, provider);
      toast.success('Successfully logged in!');
      return result.user;
    } catch (error) {
      toast.error('Failed to log in: ' + error.message);
      throw error;
    }
  };

  // Email/password sign-in
  const loginWithEmail = async (email, password) => {
    try {
      if (localAuthFallbackEnabled && !auth) {
        const localUser = createLocalAuthUser();
        setCurrentUser(localUser);
        setIsAdmin(true);
        toast.success('Signed in with local development auth.');
        return localUser;
      }
      requireAuth();
      const result = await signInWithEmailAndPassword(auth, email, password);
      toast.success('Successfully logged in!');
      return result.user;
    } catch (error) {
      const msg = friendlyAuthError(error.code);
      toast.error(msg);
      throw error;
    }
  };

  // Email/password sign-up
  const signUpWithEmail = async (email, password) => {
    try {
      requireAuth();
      const result = await createUserWithEmailAndPassword(auth, email, password);
      toast.success('Account created! Welcome.');
      return result.user;
    } catch (error) {
      const msg = friendlyAuthError(error.code);
      toast.error(msg);
      throw error;
    }
  };

  // Password reset email
  const resetPassword = async (email) => {
    try {
      requireAuth();
      await sendPasswordResetEmail(auth, email);
      toast.success('Password reset email sent — check your inbox.');
    } catch (error) {
      const msg = friendlyAuthError(error.code);
      toast.error(msg);
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (localAuthFallbackEnabled && !auth) {
        setCurrentUser(null);
        setIsAdmin(false);
        toast.success('Successfully logged out!');
        return;
      }
      requireAuth();
      await signOut(auth);
      toast.success('Successfully logged out!');
    } catch (error) {
      toast.error('Failed to log out: ' + error.message);
      throw error;
    }
  };

  const value = {
    currentUser,
    isAdmin,
    isLoading,
    db: E2E_AUTH_ENABLED ? null : db,
    login,
    loginWithEmail,
    signUpWithEmail,
    resetPassword,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Map Firebase error codes to readable messages
function friendlyAuthError(code) {
  switch (code) {
    case 'auth/user-not-found':      return 'No account found with that email.';
    case 'auth/wrong-password':      return 'Incorrect password. Try again or reset it.';
    case 'auth/invalid-credential':  return 'Incorrect email or password.';
    case 'auth/email-already-in-use':return 'An account with that email already exists.';
    case 'auth/weak-password':       return 'Password must be at least 6 characters.';
    case 'auth/invalid-email':       return 'Please enter a valid email address.';
    case 'auth/too-many-requests':   return 'Too many attempts. Please wait a moment and try again.';
    default:                         return 'Authentication error. Please try again.';
  }
}
